import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Typography from "@tiptap/extension-typography";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import { useEffect } from "react";
import type * as Y from "yjs";
import { useT } from "@/lib/i18n";
import { useBoard } from "../BoardContext";
import { useBoardUI } from "../store";

/** Collaborative rich-text note body. One Tiptap instance per visible note; editable only while this card is being edited. */
export function NoteBody({ id, fragment, fg }: { id: string; fragment: Y.XmlFragment; fg: string }) {
  const { handle, user, canEdit } = useBoard();
  const editing = useBoardUI((s) => s.editingId === id);
  const setEditing = useBoardUI((s) => s.setEditing);
  const t = useT();
  const editor = useEditor(
    {
      editable: editing && canEdit,
      immediatelyRender: true,
      shouldRerenderOnTransaction: false,
      extensions: [
        StarterKit.configure({ undoRedo: false, link: false, underline: false }),
        Underline,
        TextStyle,
        Color,
        Highlight.configure({ multicolor: true }),
        TextAlign.configure({ types: ["heading", "paragraph"] }),
        Typography,
        Link.configure({ openOnClick: !editing, autolink: true, defaultProtocol: "https" }),
        TaskList,
        TaskItem.configure({ nested: true }),
        Placeholder.configure({ placeholder: t.notePlaceholder }),
        Collaboration.configure({ fragment }),
        CollaborationCaret.configure({
          provider: handle.provider,
          user: { name: user.name, color: user.color },
        }),
      ],
      editorProps: { attributes: { class: "prose-note outline-none" } },
      onBlur: ({ event }) => {
        // Keep editing while the bubble menu is being used.
        const to = event?.relatedTarget as HTMLElement | null;
        if (to?.closest("[data-bubble]")) return;
        if (useBoardUI.getState().editingId === id) setEditing(null);
      },
    },
    [fragment],
  );
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(editing && canEdit);
    if (editing && canEdit) editor.commands.focus("end");
  }, [editing, editor, canEdit]);
  const zoom = useBoardUI((s) => s.camera.z);
  return (
    <div
      className="px-3 py-2 text-[14px] leading-[1.45]"
      style={{ color: fg }}
      onPointerDown={(e) => editing && e.stopPropagation()}
    >
      {editor && editing && canEdit ? <FormatMenu editor={editor} zoom={zoom} /> : null}
      <EditorContent editor={editor} />
    </div>
  );
}

function FormatMenu({ editor, zoom }: { editor: NonNullable<ReturnType<typeof useEditor>>; zoom: number }) {
  const t = useT();
  const st = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive("bold"),
      italic: e.isActive("italic"),
      underline: e.isActive("underline"),
      strike: e.isActive("strike"),
      h1: e.isActive("heading", { level: 1 }),
      h2: e.isActive("heading", { level: 2 }),
      bullet: e.isActive("bulletList"),
      ordered: e.isActive("orderedList"),
      task: e.isActive("taskList"),
      quote: e.isActive("blockquote"),
      code: e.isActive("codeBlock"),
      highlight: e.isActive("highlight"),
      link: e.isActive("link"),
      center: e.isActive({ textAlign: "center" }),
    }),
  });
  const btn = (label: string, active: boolean, run: () => void, cls = "") => (
    <button
      type="button"
      title={label}
      aria-label={label}
      onMouseDown={(e) => e.preventDefault()}
      onClick={run}
      className={`flex h-7 min-w-7 items-center justify-center rounded-sm px-1.5 text-[12px] ${active ? "bg-accent text-bg" : "text-muted hover:bg-panel-2 hover:text-text"} ${cls}`}
    >
      {label}
    </button>
  );
  const chain = () => editor.chain().focus();
  return (
    <BubbleMenu editor={editor} options={{ placement: "top-start", offset: 8 }}>
      <div
        data-bubble
        className="flex flex-wrap items-center gap-0.5 rounded-sm border border-border bg-panel p-1 shadow-panel"
        style={{ transform: `scale(${1 / zoom})`, transformOrigin: "0 100%" }}
      >
        {btn("B", st.bold, () => chain().toggleBold().run(), "font-bold")}
        {btn("I", st.italic, () => chain().toggleItalic().run(), "italic")}
        {btn("U", st.underline, () => chain().toggleUnderline().run(), "underline")}
        {btn("S", st.strike, () => chain().toggleStrike().run(), "line-through")}
        <span className="mx-0.5 h-5 border-l border-border" />
        {btn("H1", st.h1, () => chain().toggleHeading({ level: 1 }).run())}
        {btn("H2", st.h2, () => chain().toggleHeading({ level: 2 }).run())}
        {btn("•", st.bullet, () => chain().toggleBulletList().run())}
        {btn("1.", st.ordered, () => chain().toggleOrderedList().run())}
        {btn("☑", st.task, () => chain().toggleTaskList().run())}
        {btn("❝", st.quote, () => chain().toggleBlockquote().run())}
        {btn("</>", st.code, () => chain().toggleCodeBlock().run())}
        <span className="mx-0.5 h-5 border-l border-border" />
        {btn("◧", st.highlight, () => chain().toggleHighlight({ color: "#e9c56a" }).run())}
        {btn("≡", st.center, () =>
          st.center ? chain().setTextAlign("left").run() : chain().setTextAlign("center").run(),
        )}
        {btn(t.linkShort ?? "Link", st.link, () => {
          if (st.link) return chain().unsetLink().run();
          const url = prompt("URL", "https://");
          if (url) chain().setLink({ href: url }).run();
        })}
      </div>
    </BubbleMenu>
  );
}
